import React, { useState, useCallback, useMemo } from "react"
import { VariantRenderProps } from "../Common/Render/Render"
import { Container } from "../Common/Container/Container"
import { JSONInput } from "../Common/JSON/JSON"
import { InputLabel } from "dash-styled"
import { SDK } from "../Common/SDK/SDK"
import { JSONButtons } from "../Common/JSON/JSONButtons"
import { CSVInput } from "../Common/CSV/CSV"
import { CSVButtons } from "../Common/CSV/CSVButtons"
import {
    JobSubmitData,
    convertInputErrorPath
} from "../../../../../../Models/Client/JobSubmitData"
import { InputSchemaForm } from "../InputSchemaForm/InputSchemaForm"
import { isNested } from "../../../../../../Helpers/Schema/Schema"
import { isComplexMultiFieldType } from "universal-schema/lib"

interface Props extends VariantRenderProps {
    orgUsername: string
    appID: string | undefined
    id: string
    initialJsonValue: unknown[]
}

export const MultiField: React.FC<Props> = ({
    orgUsername,
    appID,
    id,
    data,
    schema,
    uiSchema,
    onChangeMode,
    activeMode,
    errors,
    onChange,
    testId,
    single,
    parseState,
    freshImport,
    initialJsonValue,
    initiateSubmit,
    stopSubmit,
    onLoadedImport,
    onDownloadCSVExample,
    onDownloadJSONExample,
    onClearFile,
    onDropFile,
    handleUploadBlob
}: Props) => {
    const [dirty, setDirty] = useState<boolean>(false)
    const hasNestedObject = useMemo(() => isNested(schema), [schema])

    /**
     * If all prop schemas a either simple or uploadable,
     * render using array of object input.
     * Otherwise, it is complex and just render json input
     */
    const handleChange = useCallback(
        (newValue: JobSubmitData, errors?: unknown[]) => {
            if (JSON.stringify(newValue) !== JSON.stringify(data)) {
                if (!dirty) setDirty(true)
                onChange(newValue, errors)
            }
        },
        [data, dirty, onChange]
    )

    const handleSubmit = useCallback(
        (data: JobSubmitData, errors?: unknown[], canSubmit?: boolean) => {
            onChange(data, errors, canSubmit)
        },
        [onChange]
    )

    const errorsForDisplay = useMemo(
        () =>
            dirty && errors.length > 0 && parseState.type === "idle"
                ? errors
                      .filter(({ keyword }) => keyword !== "minItems")
                      .flatMap(convertInputErrorPath)
                : [],
        [dirty, errors, parseState.type]
    )
    const hasComplexMultiField = useMemo(
        () => isComplexMultiFieldType(uiSchema),
        [uiSchema]
    )

    if (hasComplexMultiField && activeMode !== "json") {
        onChangeMode("json")
        return null
    }

    switch (activeMode) {
        case "input":
            return (
                <Container
                    onClick={onChangeMode}
                    active={activeMode}
                    variant="simple"
                    noSDKMode={appID === undefined}
                    testId={testId}
                    single={single}
                    allowCSV={!hasNestedObject}
                >
                    <InputSchemaForm
                        testId={testId}
                        single={single}
                        schema={schema}
                        data={data}
                        uiSchema={uiSchema}
                        blobUploader={handleUploadBlob}
                        onChange={handleChange}
                        initiateSubmit={initiateSubmit}
                        stopSubmit={stopSubmit}
                        onSubmit={handleSubmit}
                    />
                </Container>
            )
        case "csv":
            return (
                <Container
                    onClick={onChangeMode}
                    active={activeMode}
                    variant="simple"
                    noSDKMode={appID === undefined}
                    testId={testId}
                    single={single}
                    allowCSV={!hasNestedObject}
                >
                    <div className="p-6 pb-0">
                        <CSVButtons
                            layout="absolute"
                            single={single}
                            orgUsername={orgUsername}
                            onDownloadExample={onDownloadCSVExample}
                        />
                        <InputLabel
                            title="Your CSV file"
                            isValid={dirty && errors.length === 0}
                        />
                    </div>
                    <CSVInput
                        testId={testId}
                        parseState={parseState}
                        single={single}
                        onDropFile={onDropFile}
                        onClearFile={onClearFile}
                        onDownloadExample={onDownloadCSVExample}
                    />
                </Container>
            )
        case "json":
            return (
                <Container
                    onClick={onChangeMode}
                    active={activeMode}
                    variant={
                        hasNestedObject || hasComplexMultiField
                            ? "complex"
                            : "simple"
                    }
                    noSDKMode={appID === undefined}
                    testId={testId}
                    single={single}
                    allowCSV={!hasNestedObject && !hasComplexMultiField}
                >
                    <div className="p-6 pb-0">
                        <JSONButtons
                            onImport={onDropFile}
                            layout="absolute"
                            single={single}
                            orgUsername={orgUsername}
                            onDownloadExample={onDownloadJSONExample}
                        />
                        <InputLabel title="Your data" isValid={false} />
                    </div>
                    <JSONInput
                        single={single}
                        parseState={parseState}
                        onChange={handleChange}
                        testId={testId}
                        errors={errorsForDisplay}
                        data={data}
                        initialValue={initialJsonValue}
                        freshImport={freshImport}
                        onLoadedImport={onLoadedImport}
                        onClearFile={onClearFile}
                        onDownloadExample={onDownloadJSONExample}
                    />
                </Container>
            )
        case "sdk":
            if (appID === undefined) {
                return null
            }

            return (
                <Container
                    onClick={onChangeMode}
                    active={activeMode}
                    variant={hasNestedObject ? "complex" : "simple"}
                    noSDKMode={false}
                    testId={testId}
                    noPanel={true}
                    single={single}
                    allowCSV={!hasNestedObject}
                >
                    <SDK appID={appID} schema={schema} testId={testId} />
                </Container>
            )
    }
}
